import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { difficulty, portalBudget, scaleScore } from '@src/core/Difficulty';
import { GameEvent } from '@src/core/EventQueue';
import { kill, killOrbiters, NO_ENTITY, playClip, PrizeKind, randomPrizeKind, spawnBonusPickup, spawnBoss, spawnFx, spawnGem, spawnHeart, spawnMimic, spawnMine, spawnPrize, spawnSpawner, spawnSwiftPickup, spawnWeaponPickup, stripOffhand } from '@src/ecs/archetypes';
import { BossKind, ClipId, DragonTag, Enemy, EnemyKind, FxTag, Health, Lifetime, Mine, Pickup, PickupKind, Player, Position, PrevPosition, Projectile, Spawner, Sprite, SpriteId, Velocity, Weapon } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { BossState, emitParticles, ParticleKind, RoomPhase, type GameContext } from '@src/ecs/world';
import { isFinalFloor } from '@src/game/arena/FloorMap';
import { DoorKind, GATES, gateEntryPoint, generateMineSlots, generatePortalSlots, isFreeRoom, isRestRoom, mineCountForRoom, spawnerCountForTier, type GateSide } from '@src/game/arena/RoomLayout';
import { bossDef, bossKindForFloor, pickChampion } from '@src/game/BossDefs';
import { FloorModifier, floorMod } from '@src/game/FloorModifiers';
import { pickRoomFeatured } from '@src/game/SpawnRoster';
import { seedWallEnemies } from '@src/game/WallEnemies';
import { DROP_POOL, rollWeaponDrop, WeaponId } from '@src/game/weapons/WeaponDefs';

const TRANSITION_BOOT_SECONDS = 0.35;
const CLEAR_HEAL = 25;
/** Sanctum: the gates lift after this long, once the wizard has had a breath. */
const SANCTUM_REST_SECONDS = 1.2;
/** Prize wave: pickups rain in from the gates over this window after a clear. */
const PRIZE_WAVE_SECONDS = 1.5;
/** Cold open: hold the slam a beat longer, then drop portals. */
const FIRST_ENTER_SECONDS = 1.7;
const FIRST_PORTAL_DELAY = 0.48;

/**
 * Room state machine: Enter (gates slam) -> Fight -> Clear (exits open) -> Transition -> next room.
 * Rooms live on a FloorMap grid; gates lead to grid neighbors and clearing the exit
 * room finishes the floor (new grid, tier +2, full heal).
 */
export class RoomDirector implements GameSystem {
  private ctx!: GameContext;
  private readonly entry = new Float32Array(2);
  private prizeQueue = 0;
  private prizeTimer = 0;
  private supplyTimer = 0;
  private heartTimer = 0;
  private bonusTimer = 0;
  private enteredFrom: GateSide | -1 = -1;
  /** Bits of EnemyKind already announced this run. */
  private seenThreats = 0;
  private readonly walkFromX = new Float32Array(4);
  private readonly walkFromY = new Float32Array(4);
  private readonly walkToX = new Float32Array(4);
  private readonly walkToY = new Float32Array(4);
  private walkCount = 0;
  private pendingPortals = 0;
  private readonly pendingPortalSlots: Array<[number, number]> = [];
  private portalDelay = 0;
  /** Portal slots generated with the incoming mines so startRoom does not roll a second layout. */
  private readonly stagedPortalSlots: Array<[number, number]> = [];
  private layoutStaged = false;
  /** Seconds left on the rumble build before the boss lands (-1 = not building). */
  private bossBuild = -1;
  /** Last boss summoned this run, so champions never repeat back to back. */
  private lastBoss: BossKind | -1 = -1;
  /** Floor whose rule has already been called by the announcer. */
  private announcedFloor = 0;
  /** Number of cleared rooms since the last commercial break. */
  private clearedRoomsCount = 0;
  /** Next commercial break triggers after this many rooms cleared (2 to 3). */
  private commercialInterval = 2;
  private commercialPending = false;
  private commercialActive = false;
  public onCommercialBreak?: (onComplete: () => void) => void;
  private deathCinematicActive = false;
  public onDeathCinematic?: (onComplete: () => void) => void;
  private victoryCinematicActive = false;
  public onVictoryCinematic?: (onComplete: () => void) => void;
  /** Pause for the CONTINUE? beat. Credit is spent only if the player accepts. */
  public onContinuePrompt?: (onComplete: () => void) => void;

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  /**
   * Tear down the active run without starting a new one (return to title).
   * Entities are flagged for reap; phase is left alone so the HUD does not
   * flash Game Over under the title — startRun() rebuilds everything next.
   */
  abandonRun(): void {
    this.pendingPortals = 0;
    this.pendingPortalSlots.length = 0;
    this.portalDelay = 0;
    this.stagedPortalSlots.length = 0;
    this.layoutStaged = false;
    this.prizeQueue = 0;
    this.prizeTimer = 0;
    this.supplyTimer = 0;
    this.heartTimer = 0;
    this.bonusTimer = 0;
    this.bossBuild = -1;
    this.seenThreats = 0;
    this.lastBoss = -1;
    this.announcedFloor = 0;
    this.clearedRoomsCount = 0;
    this.commercialPending = false;
    this.commercialActive = false;
    this.deathCinematicActive = false;
    this.victoryCinematicActive = false;
    this.ctx.continues = 0;
    this.ctx.continuePrompt = false;
    this.clearArena();
  }

  /**
   * Fresh run: resets score and tier and routes through Transition so the old
   * arena is reaped before room one spawns (keeps pool caps honest).
   */
  startRun(): void {
    const ctx = this.ctx;
    ctx.rng.reseed((Date.now() ^ ((performance.now() * 1000) >>> 0) ^ ((Math.random() * 0xffffffff) >>> 0)) >>> 0);
    ctx.score = 0;
    ctx.kills = 0;
    ctx.combo.count = 0;
    ctx.combo.multiplier = 1;
    ctx.combo.best = 0;
    ctx.room.tier = 0;
    ctx.room.roomIndex = 0;
    ctx.room.featured = EnemyKind.Imp;
    this.seenThreats = 0;
    ctx.floor.floor = 0;
    ctx.floor.generate(ctx.rng);
    ctx.room.door = ctx.floor.currentKind();
    this.enteredFrom = -1;
    this.pendingPortals = 0;
    this.pendingPortalSlots.length = 0;
    this.portalDelay = 0;
    this.stagedPortalSlots.length = 0;
    this.layoutStaged = false;
    this.lastBoss = -1;
    this.announcedFloor = 0;
    this.clearedRoomsCount = 0;
    this.commercialInterval = ctx.rng.int(2, 3);
    this.commercialPending = false;
    this.commercialActive = false;
    this.deathCinematicActive = false;
    this.victoryCinematicActive = false;
    ctx.continues = difficulty().continues;
    ctx.continuePrompt = false;
    this.clearArena();
    this.revivePlayers();
    this.setPhase(RoomPhase.Transition);
  }

  update(dt: number): void {
    const ctx = this.ctx;
    const room = ctx.room;
    room.phaseTime += dt;

    switch (room.phase) {
      case RoomPhase.Enter:
        this.flushOpeningPortals(dt);
        const enterNeed = this.isColdOpen() ? FIRST_ENTER_SECONDS : Config.room.enterDuration;
        if (room.phaseTime >= enterNeed) this.setPhase(RoomPhase.Fight);
        break;
      case RoomPhase.Fight:
        room.fightTime += dt;
        if (this.allPlayersDead()) {
          if (ctx.continuePrompt) break;
          if (ctx.continues > 0 && this.onContinuePrompt) {
            this.beginContinue();
            break;
          }
          this.setPhase(RoomPhase.GameOver);
          this.beginDeathCinematic();
          break;
        }
        if (isRestRoom(room.door)) {
          // Sanctum: nothing to fight. The gates lift once the wizard has had a breath.
          if (room.fightTime >= SANCTUM_REST_SECONDS) this.onRoomCleared();
          break;
        }
        this.tickSupplyDrops(dt);
        if (room.spawnersAlive === 0) {
          // Every room holds a headliner: portals down, the rune circle charges for a beat
          // (stragglers get one last second), then the boss lands. The room clears only
          // once the boss is dead and its body has finished burning off. A chest that has
          // not woken yet is furniture and does not hold the gates.
          if (room.bossState === BossState.None) this.beginBossBuild();
          else if (room.bossState === BossState.Incoming) this.tickBossBuild(dt);
          else if (room.bossState === BossState.Dead && this.liveEnemyCount() === 0) this.onRoomCleared();
        }
        break;
      case RoomPhase.Clear:
        this.tickPrizeWave(dt);
        this.checkExit();
        break;
      case RoomPhase.Transition:
        if (this.commercialActive) {
          room.phaseTime -= dt;
          break;
        }
        this.tickWalk(dt);
        const need = room.shiftX !== 0 || room.shiftY !== 0 ? Config.room.walkSeconds : TRANSITION_BOOT_SECONDS;
        if (room.phaseTime >= need) this.nextRoom();
        break;
      case RoomPhase.GameOver:
      case RoomPhase.Victory:
        break;
      default:
        break;
    }
  }

  private setPhase(phase: RoomPhase): void {
    this.ctx.room.phase = phase;
    this.ctx.room.phaseTime = 0;
  }

  private allPlayersDead(): boolean {
    const players = query(this.ctx.world, [Player]);
    if (players.length === 0) return false;
    for (let i = 0; i < players.length; i++) if (!Player.dead[players[i]]) return false;
    return true;
  }

  /** Enemies that hold the gates shut: everything except a mimic still posing as a chest. */
  private liveEnemyCount(): number {
    const enemies = query(this.ctx.world, [Enemy]);
    let n = 0;
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (Enemy.kind[e] === EnemyKind.Mimic && Enemy.mode[e] < 2) continue;
      n++;
    }
    return n;
  }

  private startRoom(): void {
    const ctx = this.ctx;
    const room = ctx.room;
    const floor = ctx.floor;
    room.fightTime = 0;
    room.gatesOpen = false;
    room.dragonWarned = false;
    room.dragonEid = -1;
    room.bossEid = -1;
    room.bossState = BossState.None;
    room.bossBuild = 0;
    this.bossBuild = -1;
    room.door = floor.currentKind();
    // Exit rooms get the floor's archmage; everything else draws a champion from the roster.
    room.bossKind = room.door === DoorKind.Exit ? bossKindForFloor(floor.floor) : pickChampion(ctx.rng, floor.floor, room.roomIndex, this.lastBoss);
    room.chosenGate = -1;
    room.entrySide = -1;
    room.shiftX = 0;
    room.shiftY = 0;
    room.panX = 0;
    room.panY = 0;
    room.spawnersAlive = 0;
    room.featured = pickRoomFeatured(room.tier, room.door, ctx.rng, floor.modifier);
    ctx.difficulty = 1 + (room.tier - 1) * 0.12;
    this.supplyTimer = Config.pickups.supplyFirstSeconds;
    this.heartTimer = Config.pickups.supplyHeartSeconds * 0.5;
    this.bonusTimer = Config.pickups.bonusFirstSeconds;
    const rule = floorMod(floor.modifier);

    // Enter opposite the gate we left through, like walking into the next Smash TV room.
    let ex = 0;
    let ey = 0;
    if (this.enteredFrom !== -1) {
      gateEntryPoint(this.enteredFrom, this.entry);
      ex = this.entry[0];
      ey = this.entry[1];
    }

    // Rest rooms and the Champion's Ring have no portals; a Fortified floor thins the rest.
    // Walk-ins reuse the slots rolled with the incoming mines so the two layouts stay aligned.
    const opening = this.isColdOpen();
    let slots: Array<[number, number]>;
    if (this.layoutStaged) {
      slots = this.stagedPortalSlots;
      this.layoutStaged = false;
    } else {
      const rawPortals = spawnerCountForTier(room.tier, room.door);
      const count = rawPortals <= 0 ? 0 : portalBudget(Math.max(1, rawPortals + rule.portalBonus));
      slots = count > 0 ? generatePortalSlots(count, ctx.rng, ex, ey, opening) : [];
    }
    if (opening) {
      this.pendingPortalSlots.length = 0;
      for (let i = 0; i < slots.length; i++) this.pendingPortalSlots.push(slots[i]);
      this.pendingPortals = slots.length;
      this.portalDelay = FIRST_PORTAL_DELAY;
    } else {
      for (let i = 0; i < slots.length; i++) {
        spawnSpawner(ctx, slots[i][0], slots[i][1]);
      }
    }

    this.plantMines(ex, ey, slots);
    this.dressRoom(ex, ey);

    // Gates advertise the real neighbor behind them; grid edges are sealed.
    for (let g = 0; g < 4; g++) room.doorKinds[g] = floor.doorKind(g as GateSide);
    seedWallEnemies(ctx);

    const players = query(ctx.world, [Player, Position, Velocity]);
    for (let i = 0; i < players.length; i++) {
      const eid = players[i];
      Position.x[eid] = ex + (i - (players.length - 1) / 2) * 1.5;
      Position.y[eid] = ey;
      // The walk just rebased the world (camera and next room snap to the origin).
      // Pin PrevPosition so render interpolation does not smear across the old room.
      PrevPosition.x[eid] = Position.x[eid];
      PrevPosition.y[eid] = Position.y[eid];
      Velocity.x[eid] = 0;
      Velocity.y[eid] = 0;
      Player.cornerTime[eid] = 0;
      Player.idleTime[eid] = 0;
      Sprite.pop[eid] = opening ? 1.15 : 0.6;
      if (opening) Sprite.flash[eid] = 0.9;
    }

    this.setPhase(RoomPhase.Enter);
    if (opening) this.playOpeningSlam(ex, ey);
    ctx.events.push(GameEvent.RoomEnter, room.roomIndex, room.door, 0, 0);
    // First room on a new floor: call the floor rule (if there is one) before anything else.
    if (floor.floor !== this.announcedFloor) {
      this.announcedFloor = floor.floor;
      if (floor.modifier !== FloorModifier.None) ctx.events.push(GameEvent.FloorModifier, floor.modifier, floor.floor, 0, 0);
    }
    if (room.door === DoorKind.Arena) ctx.events.push(GameEvent.ArenaStart, room.bossKind, room.roomIndex, 0, 0);
    if (room.door === DoorKind.Sanctum) {
      const players = query(ctx.world, [Player]);
      ctx.events.push(GameEvent.SanctumRest, players.length > 0 ? players[0] : -1, 0, 0, 0);
    }
    const bit = 1 << room.featured;
    if (room.door !== DoorKind.Sanctum && room.door !== DoorKind.Arena && room.featured !== EnemyKind.Imp && (this.seenThreats & bit) === 0) {
      this.seenThreats |= bit;
      ctx.events.push(GameEvent.NewThreat, room.featured, 0, 0, 0);
    }
  }

  /**
   * Per-kind furniture: vault loot, sanctum offerings, and the mimics that hide among the
   * real chests. Nothing here spawns a portal.
   */
  private dressRoom(ex: number, ey: number): void {
    const ctx = this.ctx;
    const room = ctx.room;
    const m = Config.enemies.mimic;

    if (room.door === DoorKind.Treasure) {
      for (let g = 0; g < 14; g++) spawnGem(ctx, ctx.rng.range(-5, 5), ctx.rng.range(-3, 3), 3);
      spawnWeaponPickup(ctx, 0, -3, rollWeaponDrop(ctx.rng, Config.pickups.cataclysmTreasureChance));
      spawnBonusPickup(ctx, 2.2, -3);
      // Two chests flank the weapon. From the mimic debut one of them (later, maybe both) bites.
      const mimics = room.tier >= m.twinFromTier ? 2 : room.tier >= m.minTier ? 1 : 0;
      const leftIsMimic = mimics > 0 && ctx.rng.chance(0.5);
      if (mimics >= 2 || leftIsMimic) this.placeMimic(-2.5, -1);
      else spawnPrize(ctx, -2.5, -1, PrizeKind.GoldChest);
      if (mimics >= 2 || (mimics === 1 && !leftIsMimic)) this.placeMimic(2.5, -1);
      else spawnPrize(ctx, 2.5, -1, randomPrizeKind(ctx.rng));
      return;
    }

    if (room.door === DoorKind.Sanctum) {
      // A quiet room: a heart, a chest, a scatter of gems, and a weapon so the next fight starts armed.
      spawnHeart(ctx, 0, 0.6);
      spawnPrize(ctx, -3, -0.8, PrizeKind.GoldChest);
      spawnPrize(ctx, 3, -0.8, randomPrizeKind(ctx.rng));
      for (let g = 0; g < 8; g++) spawnGem(ctx, ctx.rng.range(-4, 4), ctx.rng.range(-2.5, 2.5), 2);
      spawnWeaponPickup(ctx, 0, -2.6, rollWeaponDrop(ctx.rng, Config.pickups.cataclysmSanctumChance));
      spawnSwiftPickup(ctx, 2.4, -2.6);
      return;
    }

    // Combat-type rooms: rarely, one chest sits somewhere off the entrance line. It is never real.
    const fights = room.door === DoorKind.Combat || room.door === DoorKind.Exit || room.door === DoorKind.Hazard || room.door === DoorKind.Gauntlet;
    if (fights && room.tier >= m.minTierCombat && ctx.rng.chance(m.combatChance)) {
      for (let tries = 0; tries < 8; tries++) {
        const x = ctx.rng.range(-7, 7);
        const y = ctx.rng.range(-3.5, 3.5);
        if (Math.hypot(x - ex, y - ey) < 5) continue;
        this.placeMimic(x, y);
        break;
      }
    }
  }

  private placeMimic(x: number, y: number): void {
    const eid = spawnMimic(this.ctx, x, y);
    if (eid === NO_ENTITY) spawnPrize(this.ctx, x, y, PrizeKind.GoldChest);
  }

  private plantMines(ex: number, ey: number, portals: ReadonlyArray<readonly [number, number]>): void {
    const ctx = this.ctx;
    if (query(ctx.world, [Mine]).length > 0) return;
    const n = mineCountForRoom(ctx.room.tier, ctx.room.door, ctx.floor.floor);
    if (n <= 0) return;
    const spots = generateMineSlots(n, ctx.rng, ex, ey, portals);
    for (let i = 0; i < spots.length; i++) spawnMine(ctx, spots[i][0], spots[i][1]);
  }

  /**
   * Next-room furniture that should already be sitting on the incoming floor during
   * the camera walk. Uses the same tier/door/floor startRoom will see after nextRoom().
   */
  private stageIncomingLayout(): void {
    const ctx = this.ctx;
    const room = ctx.room;
    const nextTier = room.tier + (isFreeRoom(room.door) ? 0 : 1);
    const rule = floorMod(ctx.floor.modifier);
    const rawPortals = spawnerCountForTier(nextTier, room.door);
    const count = rawPortals <= 0 ? 0 : portalBudget(Math.max(1, rawPortals + rule.portalBonus));
    const ex = this.entry[0];
    const ey = this.entry[1];
    const slots = count > 0 ? generatePortalSlots(count, ctx.rng, ex, ey, false) : [];
    this.stagedPortalSlots.length = 0;
    for (let i = 0; i < slots.length; i++) this.stagedPortalSlots.push(slots[i]);
    this.layoutStaged = true;

    const n = mineCountForRoom(nextTier, room.door, ctx.floor.floor);
    if (n <= 0) return;
    const spots = generateMineSlots(n, ctx.rng, ex, ey, slots);
    for (let i = 0; i < spots.length; i++) {
      spawnMine(ctx, room.shiftX + spots[i][0], room.shiftY + spots[i][1]);
    }
  }

  /** Snap staged mines from incoming-room world space back to the origin. */
  private rebaseMines(dx: number, dy: number): void {
    if (dx === 0 && dy === 0) return;
    const mines = query(this.ctx.world, [Mine, Position, PrevPosition]);
    for (let i = 0; i < mines.length; i++) {
      const eid = mines[i];
      Position.x[eid] += dx;
      Position.y[eid] += dy;
      PrevPosition.x[eid] = Position.x[eid];
      PrevPosition.y[eid] = Position.y[eid];
    }
  }

  /** First room of a run: no gate walked through yet. */
  private isColdOpen(): boolean {
    return this.enteredFrom === -1 && this.ctx.room.roomIndex === 1;
  }

  private flushOpeningPortals(dt: number): void {
    if (this.pendingPortals <= 0) return;
    this.portalDelay -= dt;
    if (this.portalDelay > 0) return;
    this.pendingPortals = 0;
    for (let i = 0; i < this.pendingPortalSlots.length; i++) {
      const [x, y] = this.pendingPortalSlots[i];
      spawnSpawner(this.ctx, x, y);
    }
    this.pendingPortalSlots.length = 0;
  }

  private playOpeningSlam(x: number, y: number): void {
    const ctx = this.ctx;
    for (let g = 0; g < GATES.length; g++) {
      const gate = GATES[g];
      emitParticles(ctx.fx, ParticleKind.GateDust, gate.x - gate.nx * 0.8, gate.y - gate.ny * 0.8, -gate.nx, -gate.ny, 1.4);
    }
    emitParticles(ctx.fx, ParticleKind.PickupSparkle, x, y, 0, 1, 1.3, 0xb04cff);
    ctx.fx.rumble = 1;
    ctx.fx.shakeX = 0.25;
    ctx.fx.shakeY = 1.2;
    ctx.fx.flash = 0.6;
    ctx.fx.hitStopMs = 70;
  }

  /**
   * The last portal just fell. Pick the rune point farthest from the wizard, light it,
   * and start the rumble build; the HUD slams the title card and the announcer calls it.
   */
  private beginBossBuild(): void {
    const ctx = this.ctx;
    const room = ctx.room;
    let px = 0;
    let py = 0;
    const players = query(ctx.world, [Player, Position]);
    for (let i = 0; i < players.length; i++) {
      if (Player.dead[players[i]]) continue;
      px = Position.x[players[i]];
      py = Position.y[players[i]];
      break;
    }
    const anchors = Config.boss.anchors;
    let best = 0;
    let bestD = -1;
    for (let i = 0; i < anchors.length; i++) {
      const d = Math.hypot(anchors[i][0] - px, anchors[i][1] - py);
      if (d <= bestD) continue;
      bestD = d;
      best = i;
    }
    room.bossX = anchors[best][0];
    room.bossY = anchors[best][1];
    room.bossState = BossState.Incoming;
    room.bossBuild = 0;
    this.bossBuild = Config.boss.champion.buildSeconds;
    room.dragonWarned = true;
    if (room.dragonEid !== -1) {
      // The wyrm leaves: this is the boss's stage now.
      DragonTag.phase[room.dragonEid] = 3;
      DragonTag.phaseTime[room.dragonEid] = 0;
      room.dragonEid = -1;
    }
    // Every portal shot still flying vanishes so the build reads clean.
    const projectiles = query(ctx.world, [Projectile]);
    for (let i = 0; i < projectiles.length; i++) kill(projectiles[i]);

    // The rune circle ignites: a ring of glyphs that outlives the build and the entrance.
    const def = bossDef(room.bossKind);
    const life = this.bossBuild + (def.archmage ? Config.boss.arriveSeconds : Config.boss.champion.arriveSeconds) + 0.3;
    const n = 14;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const fx = spawnFx(ctx, SpriteId.BossBolt, room.bossX + Math.cos(a) * 2.3, room.bossY + Math.sin(a) * 2.3 * 0.7, a + Math.PI / 2, 0.9, def.glowTint, 0.45);
      if (fx === NO_ENTITY) break;
      Lifetime.t[fx] = life;
      Sprite.alpha[fx] = 0.7;
    }
    spawnFx(ctx, SpriteId.Shockwave, room.bossX, room.bossY, 0, 2.8, def.glowTint, 0.5);
    emitParticles(ctx.fx, ParticleKind.PortalMotes, room.bossX, room.bossY, 0, 1, 1.4, def.glowTint);
    ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 60);
    ctx.fx.rumble = 1;
    ctx.fx.shakeY += 0.3;
    ctx.events.push(GameEvent.BossIncoming, room.bossKind, room.roomIndex, room.bossX, room.bossY);
  }

  /** Shake ramps and motes stream toward the rune; when the timer runs out the boss lands. */
  private tickBossBuild(dt: number): void {
    const ctx = this.ctx;
    const room = ctx.room;
    const total = Config.boss.champion.buildSeconds;
    this.bossBuild -= dt;
    const u = Math.min(1, 1 - this.bossBuild / total);
    room.bossBuild = u;
    ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.5 + u * 0.5);
    ctx.fx.shakeX += (ctx.rng.next() - 0.5) * dt * (2 + u * 8);
    ctx.fx.shakeY += (ctx.rng.next() - 0.5) * dt * (2 + u * 8);
    const def = bossDef(room.bossKind);
    if (ctx.rng.chance(dt * 26)) {
      const a = ctx.rng.next() * Math.PI * 2;
      const r = 2.5 + ctx.rng.next() * 3;
      emitParticles(ctx.fx, ParticleKind.PortalMotes, room.bossX + Math.cos(a) * r, room.bossY + Math.sin(a) * r, -Math.cos(a), -Math.sin(a), 0.8, def.glowTint);
    }
    if (this.bossBuild > 0) return;
    this.bossBuild = -1;
    room.bossBuild = 1;
    this.summonBoss();
  }

  /**
   * The rune circle is charged: stragglers are blown away by the arrival shockwave and
   * the boss spawns on the rune point. BossSystem runs the entrance itself.
   */
  private summonBoss(): void {
    const ctx = this.ctx;
    const room = ctx.room;
    const def = bossDef(room.bossKind);

    // Anything still standing from the portal fight is swept off the stage.
    const enemies = query(ctx.world, [Enemy, Position]);
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (Enemy.kind[e] === EnemyKind.Boss) continue;
      // A chest still posing as a chest stays; it is scenery until the wizard gets greedy.
      if (Enemy.kind[e] === EnemyKind.Mimic && Enemy.mode[e] < 2) continue;
      emitParticles(ctx.fx, ParticleKind.HitSpark, Position.x[e], Position.y[e], 0, 1, 0.9, def.glowTint);
      spawnFx(ctx, SpriteId.Smoke, Position.x[e], Position.y[e], ctx.rng.next() * Math.PI * 2, 1.3, def.glowTint, 0.5);
      kill(e);
    }
    spawnFx(ctx, SpriteId.Shockwave, room.bossX, room.bossY, 0, 7, def.glowTint, 0.55);

    const champ = Config.boss.champion;
    const finale = room.door === DoorKind.Exit && isFinalFloor(ctx.floor.floor);
    const roomMult = room.door === DoorKind.Treasure ? champ.treasureHpMult : room.door === DoorKind.Arena ? champ.arenaHpMult : 1;
    const hpMult = roomMult * (finale ? 1.45 : 1);
    const eid = spawnBoss(ctx, room.bossKind, room.bossX, room.bossY, hpMult);
    if (eid === NO_ENTITY) {
      // Pool is somehow full (previous boss still dissolving): no headliner, just clear.
      room.bossState = BossState.Dead;
      return;
    }
    room.bossEid = eid;
    room.bossState = BossState.Alive;
    this.lastBoss = room.bossKind as BossKind;
    // Fresh supply cadence so a weapon shows up early in the duel.
    this.supplyTimer = Math.min(this.supplyTimer, 7);
    this.heartTimer = Math.min(this.heartTimer, 9);
    ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 90);
    ctx.fx.flash = Math.max(ctx.fx.flash, 0.3);
    ctx.fx.rumble = 1;
    ctx.events.push(GameEvent.BossSpawn, eid, room.bossKind, room.bossX, room.bossY);
  }

  private onRoomCleared(): void {
    const ctx = this.ctx;
    const room = ctx.room;
    room.gatesOpen = true;
    if (room.dragonEid !== -1) {
      // The wyrm leaves on its own: fly off and fade (DragonSystem handles the exit).
      DragonTag.phase[room.dragonEid] = 3;
      DragonTag.phaseTime[room.dragonEid] = 0;
      room.dragonEid = -1;
    }
    const rest = isRestRoom(room.door);
    const players = query(ctx.world, [Player, Health]);
    for (let i = 0; i < players.length; i++) {
      const eid = players[i];
      if (Player.dead[eid]) continue;
      // A sanctum heals for real; a cleared fight is a top-up.
      const heal = rest ? Health.max[eid] * 0.5 : CLEAR_HEAL * difficulty().heal;
      Health.hp[eid] = Math.min(Health.max[eid], Health.hp[eid] + Math.round(heal));
    }
    if (!rest) ctx.score += scaleScore(Math.max(0, Math.round((60 - room.fightTime) * 20)) * room.tier);
    ctx.floor.markCleared();
    if (room.door === DoorKind.Exit && isFinalFloor(ctx.floor.floor)) {
      ctx.score += scaleScore(25000);
      this.commercialPending = false;
      ctx.events.push(GameEvent.RunWon, ctx.floor.floor, ctx.score, 0, 0);
      this.setPhase(RoomPhase.Victory);
      this.beginVictoryCinematic();
      return;
    }
    if (!rest) {
      this.clearedRoomsCount++;
      if (this.clearedRoomsCount >= this.commercialInterval) {
        this.clearedRoomsCount = 0;
        this.commercialInterval = ctx.rng.int(2, 3);
        this.commercialPending = true;
      }
    }
    this.setPhase(RoomPhase.Clear);
    ctx.events.push(GameEvent.RoomCleared, room.roomIndex, Math.round(room.fightTime), rest ? 1 : 0, 0);
    // The portcullises winch up: dust shakes loose from every live gate.
    for (let g = 0; g < GATES.length; g++) {
      if (room.doorKinds[g] === DoorKind.Sealed) continue;
      const gate = GATES[g];
      emitParticles(ctx.fx, ParticleKind.GateDust, gate.x - gate.nx * 0.8, gate.y - gate.ny * 0.8, -gate.nx, -gate.ny, 1);
    }
    ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.5);

    // Prize wave: bigger on the exit room and the Champion's Ring; Bounty floors add to every wave.
    // A sanctum already put its offerings on the floor.
    const exit = room.door === DoorKind.Exit;
    const arena = room.door === DoorKind.Arena;
    this.prizeQueue = rest ? 0 : (exit ? 8 : arena ? 6 : 3 + ctx.rng.int(0, 3)) + floorMod(ctx.floor.modifier).prizeWaveBonus;
    this.prizeTimer = 0;
    if (this.prizeQueue > 0) ctx.events.push(GameEvent.PrizeWave, this.prizeQueue, exit ? 1 : 0, 0, 0);
    if (arena) {
      // The ring pays in steel: a fresh Epic Weapon lands where the champion fell.
      spawnWeaponPickup(ctx, room.bossX, room.bossY, rollWeaponDrop(ctx.rng, Config.pickups.cataclysmArenaChance));
    }
    if (exit) {
      // Every open gate now leads off the floor.
      for (let g = 0; g < 4; g++) if (room.doorKinds[g] !== DoorKind.Sealed) room.doorKinds[g] = DoorKind.Exit;
      ctx.events.push(GameEvent.FloorCleared, ctx.floor.floor, room.tier, 0, 0);
      for (let i = 0; i < players.length; i++) {
        const eid = players[i];
        if (!Player.dead[eid]) Health.hp[eid] = Health.max[eid];
      }
    }
  }

  /**
   * Mid-fight supply: Epic Weapons drift in through a gate on a cadence so you are
   * never stuck with the wand until a portal dies, and a rune heart floats in when
   * you are hurting. One of each on the floor at a time, at most.
   */
  private tickSupplyDrops(dt: number): void {
    const ctx = this.ctx;
    const p = Config.pickups;
    this.supplyTimer -= dt;
    this.heartTimer -= dt;

    let weaponOut = false;
    let heartOut = false;
    let shieldOut = false;
    let swiftOut = false;
    const pickups = query(ctx.world, [Pickup]);
    for (let i = 0; i < pickups.length; i++) {
      const k = Pickup.kind[pickups[i]];
      if (k === PickupKind.Weapon) weaponOut = true;
      else if (k === PickupKind.Heart) heartOut = true;
      else if (k === PickupKind.Shield) shieldOut = true;
      else if (k === PickupKind.Swift) swiftOut = true;
    }

    if (this.supplyTimer <= 0 && !weaponOut) {
      this.supplyTimer = p.supplyIntervalSeconds;
      // Prefer something the player is not already holding. Cataclysm is allowed to repeat.
      const players = query(ctx.world, [Player, Weapon]);
      let weapon = rollWeaponDrop(ctx.rng, p.cataclysmSupplyChance);
      if (weapon !== WeaponId.Cataclysm && DROP_POOL.length > 0) {
        for (let tries = 0; tries < 3 && players.some((pid) => Weapon.typeId[pid] === weapon); tries++) {
          weapon = DROP_POOL[ctx.rng.int(0, DROP_POOL.length - 1)];
        }
      }
      this.driftIn((x, y) => spawnWeaponPickup(ctx, x, y, weapon), weapon);
    }

    if (this.heartTimer <= 0 && !heartOut) {
      this.heartTimer = p.supplyHeartSeconds;
      const players = query(ctx.world, [Player, Health]);
      let hurting = false;
      for (let i = 0; i < players.length; i++) {
        const pid = players[i];
        if (!Player.dead[pid] && Health.hp[pid] < Health.max[pid] * p.supplyHeartBelow) hurting = true;
      }
      if (hurting) this.driftIn((x, y) => spawnHeart(ctx, x, y), -1);
    }

    this.bonusTimer -= dt;
    if (this.bonusTimer <= 0 && (!shieldOut || !swiftOut)) {
      this.bonusTimer = p.bonusIntervalSeconds;
      if (!shieldOut && !swiftOut) this.driftIn((x, y) => spawnBonusPickup(ctx, x, y), -2);
      else if (!shieldOut) this.driftIn((x, y) => spawnBonusPickup(ctx, x, y), -2);
      else this.driftIn((x, y) => spawnSwiftPickup(ctx, x, y), -3);
    }
  }

  /** Spawns a pickup just inside a random live gate and sends it gliding toward the middle. */
  private driftIn(spawn: (x: number, y: number) => number, payload: number): void {
    const ctx = this.ctx;
    let g = ctx.rng.int(0, 3);
    for (let tries = 0; tries < 4 && ctx.room.doorKinds[g] === DoorKind.Sealed; tries++) g = (g + 1) % 4;
    const gate = GATES[g];
    const sx = gate.x - gate.nx * 0.9;
    const sy = gate.y - gate.ny * 0.9;
    const eid = spawn(sx, sy);
    if (eid === -1) return;
    // Aim at a point in the middle third of the room so it ends up somewhere reachable.
    const tx = ctx.rng.range(-5, 5);
    const ty = ctx.rng.range(-2.5, 2.5);
    const dx = tx - sx;
    const dy = ty - sy;
    const d = Math.hypot(dx, dy) || 1;
    const speed = Config.pickups.supplyDriftSpeed;
    Velocity.x[eid] = (dx / d) * speed;
    Velocity.y[eid] = (dy / d) * speed;
    // Glide most of the way, then let drag settle it.
    Pickup.drift[eid] = (d * 0.75) / speed;
    emitParticles(ctx.fx, ParticleKind.PickupSparkle, sx, sy, -gate.nx, -gate.ny, 1.0, payload === -1 ? 0xff7a9a : 0xfff0a0);
    ctx.events.push(GameEvent.SupplyDrop, g, payload, sx, sy);
  }

  /** Drops prizes and gems in from random open gates, spaced across the wave window. */
  private tickPrizeWave(dt: number): void {
    if (this.prizeQueue <= 0) return;
    const ctx = this.ctx;
    this.prizeTimer -= dt;
    if (this.prizeTimer > 0) return;
    this.prizeTimer = PRIZE_WAVE_SECONDS / 6;
    this.prizeQueue--;

    // Pick an unsealed gate to throw from.
    let g = ctx.rng.int(0, 3);
    for (let tries = 0; tries < 4 && ctx.room.doorKinds[g] === DoorKind.Sealed; tries++) g = (g + 1) % 4;
    const gate = GATES[g];
    const sx = gate.x - gate.nx * 1.5;
    const sy = gate.y - gate.ny * 1.5;
    const tx = ctx.rng.range(-8, 8);
    const ty = ctx.rng.range(-4, 4);
    const eid = ctx.rng.chance(Config.pickups.bonusWaveChance)
      ? spawnBonusPickup(ctx, sx, sy)
      : ctx.rng.chance(0.55)
        ? spawnPrize(ctx, sx, sy, randomPrizeKind(ctx.rng))
        : spawnGem(ctx, sx, sy, 3);
    if (eid === -1) return;
    // Lob toward a random landing spot; MovementSystem's pickup drag slows it down.
    Velocity.x[eid] = (tx - sx) * 1.6;
    Velocity.y[eid] = (ty - sy) * 1.6;
    emitParticles(ctx.fx, ParticleKind.PickupSparkle, sx, sy, -gate.nx, -gate.ny, 0.8);
  }

  private checkExit(): void {
    const ctx = this.ctx;
    const room = ctx.room;
    if (room.phaseTime < Config.room.clearDuration) return;
    const players = query(ctx.world, [Player, Position]);
    for (let i = 0; i < players.length; i++) {
      const eid = players[i];
      if (Player.dead[eid]) continue;
      const px = Position.x[eid];
      const py = Position.y[eid];
      for (let g = 0; g < GATES.length; g++) {
        if (room.doorKinds[g] === DoorKind.Sealed) continue;
        const gate = GATES[g];
        // Players are clamped to the inner bounds, so allow a radius-sized reach into the doorway.
        const reach = Config.player.radius + Config.room.wallThickness;
        const inSpan = Math.abs(px - gate.x) <= gate.hw + reach && Math.abs(py - gate.y) <= gate.hh + reach;
        // Also require that the player is pressing into the doorway.
        const pushing = Player.moveX[eid] * gate.nx + Player.moveY[eid] * gate.ny > 0.5;
        if (!inSpan || !pushing) continue;
        this.leaveThrough(g as GateSide);
        return;
      }
    }
  }

  private leaveThrough(side: GateSide): void {
    const ctx = this.ctx;
    const room = ctx.room;
    const gate = GATES[side];
    room.chosenGate = side;
    this.enteredFrom = ((side + 2) % 4) as GateSide;
    room.entrySide = this.enteredFrom;
    if (ctx.floor.exitCleared) {
      ctx.floor.generate(ctx.rng);
      room.tier += 1;
    } else {
      ctx.floor.move(side);
    }
    room.door = ctx.floor.currentKind();
    for (let g = 0; g < 4; g++) room.nextDoorKinds[g] = ctx.floor.doorKind(g as GateSide);
    // New-floor entrance sits on a grid edge, so that side is Sealed. Keep the
    // doorway we are walking through painted as a live gate for the pan.
    if (room.nextDoorKinds[this.enteredFrom] === DoorKind.Sealed) {
      room.nextDoorKinds[this.enteredFrom] = DoorKind.Combat;
    }

    // Next room sits flush against this one along the gate axis.
    room.shiftX = gate.nx * Config.room.width;
    room.shiftY = gate.ny * Config.room.height;
    room.panX = 0;
    room.panY = 0;

    gateEntryPoint(this.enteredFrom, this.entry);
    const destX = room.shiftX + this.entry[0];
    const destY = room.shiftY + this.entry[1];
    const players = query(ctx.world, [Player, Position, Velocity]);
    this.walkCount = Math.min(players.length, this.walkFromX.length);
    for (let i = 0; i < this.walkCount; i++) {
      const eid = players[i];
      this.walkFromX[i] = Position.x[eid];
      this.walkFromY[i] = Position.y[eid];
      this.walkToX[i] = destX + (i - (this.walkCount - 1) / 2) * 1.5;
      this.walkToY[i] = destY;
    }

    this.setPhase(RoomPhase.Transition);
    ctx.events.push(GameEvent.RoomNext, room.roomIndex + 1, room.door, gate.x, gate.y);
    this.clearArena();
    this.stageIncomingLayout();

    if (this.commercialPending && this.onCommercialBreak) {
      this.commercialPending = false;
      this.commercialActive = true;
      this.onCommercialBreak(() => {
        this.commercialActive = false;
      });
    }
  }

  /** Ease the wizard and camera from this room into the one sitting beside it. */
  private tickWalk(dt: number): void {
    const room = this.ctx.room;
    if (room.shiftX === 0 && room.shiftY === 0) return;
    const u = Math.min(1, room.phaseTime / Config.room.walkSeconds);
    const t = u * u * (3 - 2 * u);
    room.panX = room.shiftX * t;
    room.panY = room.shiftY * t;
    const players = query(this.ctx.world, [Player, Position, Velocity, Sprite]);
    const n = Math.min(players.length, this.walkCount);
    const invDt = dt > 0 ? 1 / dt : 0;
    for (let i = 0; i < n; i++) {
      const eid = players[i];
      const nx = this.walkFromX[i] + (this.walkToX[i] - this.walkFromX[i]) * t;
      const ny = this.walkFromY[i] + (this.walkToY[i] - this.walkFromY[i]) * t;
      Velocity.x[eid] = (nx - Position.x[eid]) * invDt;
      Velocity.y[eid] = (ny - Position.y[eid]) * invDt;
      Position.x[eid] = nx;
      Position.y[eid] = ny;
      const gate = GATES[room.chosenGate];
      Velocity.x[eid] = gate.nx * 6;
      Velocity.y[eid] = gate.ny * 6;
      if (Math.abs(gate.nx) > 0.2) Sprite.face[eid] = gate.nx < 0 ? -1 : 1;
    }
  }

  private nextRoom(): void {
    const room = this.ctx.room;
    this.rebaseMines(-room.shiftX, -room.shiftY);
    room.roomIndex++;
    room.tier += isFreeRoom(room.door) ? 0 : 1;
    this.startRoom();
  }

  triggerCommercial(): void {
    this.commercialPending = true;
  }

  isCommercialActive(): boolean {
    return this.commercialActive;
  }

  isDeathCinematicActive(): boolean {
    return this.deathCinematicActive;
  }

  isVictoryCinematicActive(): boolean {
    return this.victoryCinematicActive;
  }

  /** DEV / debug: skip to the win cinematic from any live phase. */
  debugWin(): void {
    if (this.ctx.room.phase === RoomPhase.Victory) return;
    this.commercialPending = false;
    this.commercialActive = false;
    this.ctx.events.push(GameEvent.RunWon, this.ctx.floor.floor, this.ctx.score, 0, 0);
    this.setPhase(RoomPhase.Victory);
    this.beginVictoryCinematic();
  }

  private beginContinue(): void {
    const ctx = this.ctx;
    if (ctx.continues <= 0 || ctx.continuePrompt || !this.onContinuePrompt) return;
    ctx.continuePrompt = true;
    this.onContinuePrompt(() => this.finishContinue());
  }

  /** Clear the CONTINUE? hold and stand the wizard back up. Loadout stays. */
  finishContinue(): void {
    const ctx = this.ctx;
    if (!ctx.continuePrompt) return;
    ctx.continuePrompt = false;
    if (Number.isFinite(ctx.continues) && ctx.continues > 0) ctx.continues -= 1;
    this.reviveForContinue();
  }

  /** Countdown ran out or the player gave up. Same death as a run with no continues. */
  expireContinue(): void {
    const ctx = this.ctx;
    if (!ctx.continuePrompt) return;
    ctx.continuePrompt = false;
    this.setPhase(RoomPhase.GameOver);
    this.beginDeathCinematic();
  }

  /** Full HP, brief i-frames, same floor / room / score / weapons. */
  private reviveForContinue(): void {
    const players = query(this.ctx.world, [Player, Health, Sprite, Velocity]);
    for (let i = 0; i < players.length; i++) {
      const eid = players[i];
      Player.dead[eid] = 0;
      Player.firing[eid] = 0;
      Player.dashTime[eid] = 0;
      Health.hp[eid] = Health.max[eid];
      Health.invuln[eid] = Config.player.continueInvuln;
      Velocity.x[eid] = 0;
      Velocity.y[eid] = 0;
      Sprite.id[eid] = SpriteId.Player;
      Sprite.alpha[eid] = 1;
      Sprite.squash[eid] = 0;
      Sprite.pop[eid] = Math.max(Sprite.pop[eid], 0.55);
      Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.85);
      playClip(eid, ClipId.Idle, true);
    }
  }

  private beginDeathCinematic(): void {
    if (this.deathCinematicActive || this.victoryCinematicActive || this.commercialActive || !this.onDeathCinematic) return;
    this.deathCinematicActive = true;
    this.onDeathCinematic(() => {
      this.deathCinematicActive = false;
    });
  }

  private beginVictoryCinematic(): void {
    if (this.victoryCinematicActive || this.deathCinematicActive || this.commercialActive || !this.onVictoryCinematic) return;
    this.victoryCinematicActive = true;
    this.onVictoryCinematic(() => {
      this.victoryCinematicActive = false;
    });
  }

  /** Flags every non-player entity for removal. Reaped by LifetimeSystem this tick. */
  private clearArena(): void {
    const w = this.ctx.world;
    const groups = [query(w, [Enemy]), query(w, [Spawner]), query(w, [Projectile]), query(w, [Pickup]), query(w, [DragonTag]), query(w, [FxTag]), query(w, [Mine])];
    for (let g = 0; g < groups.length; g++) {
      const list = groups[g];
      for (let i = 0; i < list.length; i++) kill(list[i]);
    }
    this.ctx.room.spawnersAlive = 0;
    this.ctx.room.dragonEid = -1;
    this.ctx.room.bossEid = -1;
    this.ctx.room.bossState = BossState.None;
    this.ctx.room.bossBuild = 0;
    this.bossBuild = -1;
    this.ctx.beams.count = 0;
    this.prizeQueue = 0;
  }

  private revivePlayers(): void {
    const players = query(this.ctx.world, [Player, Health, Sprite, Weapon]);
    for (let i = 0; i < players.length; i++) {
      const eid = players[i];
      Player.dead[eid] = 0;
      Health.hp[eid] = Health.max[eid];
      Health.invuln[eid] = 0;
      Sprite.id[eid] = SpriteId.Player;
      Sprite.alpha[eid] = 1;
      Sprite.squash[eid] = 0;
      playClip(eid, ClipId.Idle, true);
      Weapon.typeId[eid] = 0;
      Weapon.timeLeft[eid] = 0;
      Weapon.overcharge[eid] = 0;
      Weapon.cooldown[eid] = 0;
      killOrbiters(this.ctx, eid);
      stripOffhand(eid);
      Player.swiftTime[eid] = 0;
      Player.dashTime[eid] = 0;
    }
  }
}
